import { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { StoreState } from '../../redux-types';
import { RouteInformation } from '../../shared/constants/RouteInformation';
import { API } from '../../shared/constants/constants';
import axios, { AxiosError } from 'axios';
import { adaptAssistantChartPayload, AssistantChartPayload } from './chartPayloadAdapter';
import { ChatMessage } from './types';
import ChippyChatWindow from './ChippyChatWindow';
import ChippyThreadsPanel from './ChippyThreadsPanel';
import {
  appendChippyMessage,
  ChippyMessageRecord,
  ChippyMessageFeedbackSentiment,
  ChippyThread,
  createChippyThread,
  getChippyMessagesByThreadId,
  getChippyThreads,
  updateChippyMessageFeedback,
} from '../../shared/requests/chippyRequests';

type SessionState = Record<string, unknown> | null;

interface TrackerAgentChatResponse {
  response: string | null;
  error: string | null;
  session_state: SessionState;
  chart?: AssistantChartPayload | null;
}

const LOCAL_TRACKER_AGENT_CHAT_ENDPOINT = 'http://localhost:8000/chat';
const REMOTE_TRACKER_AGENT_CHAT_ENDPOINT = '/tracker-agent/chat';
const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1']);
const CHIPPY_FEEDBACK_FORM_URL = process.env.REACT_APP_CHIPPY_FEEDBACK_FORM_URL || RouteInformation.feedback.path;
const DEFAULT_THREAD_TITLE = 'New conversation';

const initialMessage: ChatMessage = {
  id: 'assistant-welcome',
  role: 'assistant',
  text: `Hi, I'm Chippy. Ask me questions summarizing data, displaying charts or creating new entries. Some upcoming features include drafting content.`,
};

const createAssistantReply = (input: string): string => {
  const normalized = input.trim();
  if (!normalized) {
    return 'I can help once you share a prompt.';
  }

  if (normalized.length < 18) {
    return `Want me to expand on "${normalized}" with a short draft and key points?`;
  }

  return `Understood. Here's a fast approach: 1) define the goal, 2) list constraints, and 3) produce a concise response with next actions.`;
};

const toThreadSortDate = (thread: ChippyThread): number => {
  const rawDate = thread.lastMessageAt || thread.updatedAt || thread.createdAt;
  if (!rawDate) return 0;
  const parsed = new Date(rawDate).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

const upsertAndSortThreads = (threads: ChippyThread[], nextThread: ChippyThread): ChippyThread[] => {
  const merged = [nextThread, ...threads.filter((thread) => thread._id !== nextThread._id)];
  return merged.sort((a, b) => toThreadSortDate(b) - toThreadSortDate(a));
};

const mapStoredMessageToChatMessage = (message: ChippyMessageRecord): ChatMessage => ({
  id: message._id,
  role: message.role,
  text: message.text ?? '',
  chart: adaptAssistantChartPayload(message.chart ?? null),
  feedback: message.feedback ?? null,
});

const ChippyPage = () => {
  const userInfo = useSelector((state: StoreState) => state.user.userInfo);
  const isLocalDeployment =
    typeof window !== 'undefined' && LOCAL_HOSTNAMES.has(window.location.hostname.toLowerCase());

  const [messages, setMessages] = useState<ChatMessage[]>([initialMessage]);
  const [threads, setThreads] = useState<ChippyThread[]>([]);
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);
  const [isLoadingThreads, setIsLoadingThreads] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);

  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionState, setSessionState] = useState<SessionState>(null);
  const [updatingFeedbackMessageId, setUpdatingFeedbackMessageId] = useState<string | null>(null);

  const pushUserMessage = (text: string) => {
    const newUserMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      text: text.trim(),
    };

    setMessages((prev) => [...prev, newUserMessage]);
  };

  const pushAssistantMessage = (text: string, chart: ChatMessage['chart'] = null) => {
    const newAssistantMessage: ChatMessage = {
      id: `assistant-${Date.now()}`,
      role: 'assistant',
      text,
      chart,
    };
    setMessages((prev) => [...prev, newAssistantMessage]);
  };

  const loadThreadMessages = async (threadId: string) => {
    setIsLoadingMessages(true);
    try {
      const { messages: storedMessages, thread } = await getChippyMessagesByThreadId(threadId);
      const nextMessages = storedMessages.length ? storedMessages.map(mapStoredMessageToChatMessage) : [initialMessage];
      setMessages(nextMessages);
      setActiveThreadId(threadId);
      setSessionState(thread?.sessionState ?? null);
    } catch (error) {
      setMessages([initialMessage]);
      setSessionState(null);
      setActiveThreadId(null);
      if (error instanceof AxiosError) {
        pushAssistantMessage(error.response?.data?.error ?? error.response?.data?.message ?? error.message);
      } else if (error instanceof Error) {
        pushAssistantMessage(error.message);
      } else {
        pushAssistantMessage('Failed to load conversation.');
      }
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const handleStartNewChat = () => {
    setActiveThreadId(null);
    setMessages([initialMessage]);
    setSessionState(null);
  };

  const handleDownloadConversation = () => {
    const conversationText = messages
      .map((message) => {
        const roleLabel = message.role === 'assistant' ? 'Chippy' : message.role === 'system' ? 'System' : 'You';
        const text = message.text?.trim() ? message.text : '[No text content]';
        const chartNote = message.chart?.chartData
          ? `\n[Chart attached${message.chart.title ? `: ${message.chart.title}` : ''}]`
          : '';
        return `${roleLabel}:\n${text}${chartNote}`;
      })
      .join('\n\n');

    const fileContent = `Chippy Conversation Export\nGenerated: ${new Date().toISOString()}\n\n${conversationText}\n`;
    const blob = new Blob([fileContent], { type: 'text/plain;charset=utf-8' });
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    link.href = blobUrl;
    link.download = `chippy-conversation-${timestamp}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  };

  useEffect(() => {
    if (!userInfo?.id) {
      setThreads([]);
      setActiveThreadId(null);
      setMessages([initialMessage]);
      setSessionState(null);
      return;
    }

    let isMounted = true;

    void (async () => {
      setIsLoadingThreads(true);
      try {
        const loadedThreads = (await getChippyThreads(50)).sort((a, b) => toThreadSortDate(b) - toThreadSortDate(a));

        if (!isMounted) {
          return;
        }

        setThreads(loadedThreads);

        if (loadedThreads.length > 0) {
          void loadThreadMessages(loadedThreads[0]._id);
        } else {
          handleStartNewChat();
        }
      } catch {
        if (!isMounted) {
          return;
        }
        setThreads([]);
        handleStartNewChat();
      } finally {
        if (isMounted) {
          setIsLoadingThreads(false);
        }
      }
    })();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.id]);

  const sendMessage = () => {
    const trimmed = input.trim();
    if (!trimmed || isTyping) {
      return;
    }

    if (!userInfo?.id || !userInfo?.tenantId) {
      pushAssistantMessage('Unable to send message because user context is missing.');
      return;
    }

    pushUserMessage(trimmed);
    setInput('');
    setIsTyping(true);

    void (async () => {
      let threadId = activeThreadId;
      let requestMessageId: string | null = null;
      let shouldPersistUserMessage = true;

      try {
        if (!threadId) {
          const createdThreadResult = await createChippyThread({
            initialMessage: trimmed,
            title: DEFAULT_THREAD_TITLE,
            sessionState,
          });
          const createdThread = createdThreadResult.thread;
          requestMessageId = createdThreadResult.initialMessage?._id ?? null;

          if (createdThread?._id) {
            threadId = createdThread._id;
            setActiveThreadId(createdThread._id);
            setThreads((prev) => upsertAndSortThreads(prev, createdThread));
            shouldPersistUserMessage = false;
          }
        }

        if (threadId && shouldPersistUserMessage) {
          const { thread: updatedThread, message: storedUserMessage } = await appendChippyMessage(threadId, {
            role: 'user',
            text: trimmed,
          });
          requestMessageId = storedUserMessage?._id ?? null;
          if (updatedThread) {
            setThreads((prev) => upsertAndSortThreads(prev, updatedThread));
          }
        }

        const payload = {
          user_input: trimmed,
          user_id: userInfo.id,
          tenant_id: userInfo.tenantId,
          session_state: sessionState,
          thread_id: threadId,
          message_id: requestMessageId,
        };

        const { data } = isLocalDeployment
          ? await axios.post<TrackerAgentChatResponse>(LOCAL_TRACKER_AGENT_CHAT_ENDPOINT, payload)
          : await API.post<TrackerAgentChatResponse>(REMOTE_TRACKER_AGENT_CHAT_ENDPOINT, payload);

        const normalizedChart = adaptAssistantChartPayload(data?.chart);
        const nextSessionState = data?.session_state ?? null;

        let assistantText = '';
        if (data?.error) {
          assistantText = data.error;
        } else if (data?.response || data?.chart) {
          assistantText = data?.response ?? '';
        } else {
          assistantText = createAssistantReply(trimmed);
        }

        pushAssistantMessage(assistantText, normalizedChart);
        setSessionState(nextSessionState);

        if (threadId) {
          const { thread: updatedThread, message: storedAssistantMessage } = await appendChippyMessage(threadId, {
            role: 'assistant',
            text: assistantText,
            chart: normalizedChart,
            sessionState: nextSessionState,
          });
          if (updatedThread) {
            setThreads((prev) => upsertAndSortThreads(prev, updatedThread));
          }
          if (storedAssistantMessage?._id) {
            setMessages((prev) =>
              prev.map((message, index) =>
                index === prev.length - 1
                  ? {
                      ...message,
                      id: storedAssistantMessage._id,
                      feedback: storedAssistantMessage.feedback ?? null,
                    }
                  : message
              )
            );
          }
        }
      } catch (error) {
        let errorMessage = 'An unexpected error occurred while contacting Chippy.';
        if (error instanceof AxiosError) {
          errorMessage = error.response?.data?.error ?? error.response?.data?.message ?? error.message;
        } else if (error instanceof Error) {
          errorMessage = error.message;
        }

        pushAssistantMessage(errorMessage);

        if (threadId) {
          try {
            const { thread: updatedThread, message: storedErrorMessage } = await appendChippyMessage(threadId, {
              role: 'assistant',
              text: errorMessage,
              sessionState,
            });
            if (updatedThread) {
              setThreads((prev) => upsertAndSortThreads(prev, updatedThread));
            }
            if (storedErrorMessage?._id) {
              setMessages((prev) =>
                prev.map((message, index) =>
                  index === prev.length - 1
                    ? {
                        ...message,
                        id: storedErrorMessage._id,
                        feedback: storedErrorMessage.feedback ?? null,
                      }
                    : message
                )
              );
            }
          } catch {
            // Keep user flow uninterrupted even if persistence fails.
          }
        }
      } finally {
        setIsTyping(false);
      }
    })();
  };

  const handleMessageFeedback = async (messageId: string, feedback: ChippyMessageFeedbackSentiment) => {
    if (!activeThreadId || !messageId || messageId.startsWith('assistant-')) {
      return;
    }

    const targetMessage = messages.find((message) => message.id === messageId);
    if (!targetMessage || targetMessage.role !== 'assistant') {
      return;
    }

    const nextFeedback = targetMessage.feedback?.sentiment === feedback ? null : feedback;
    setUpdatingFeedbackMessageId(messageId);
    try {
      const updatedMessage = await updateChippyMessageFeedback(activeThreadId, messageId, { feedback: nextFeedback });
      setMessages((prev) =>
        prev.map((message) =>
          message.id === messageId
            ? {
                ...message,
                feedback: updatedMessage.feedback ?? null,
              }
            : message
        )
      );
    } catch {
      // Do not interrupt chat flow if feedback persistence fails.
    } finally {
      setUpdatingFeedbackMessageId(null);
    }
  };

  const handleMessageFeedbackComment = async (messageId: string, feedbackComment: string | null) => {
    if (!activeThreadId || !messageId || messageId.startsWith('assistant-')) {
      return;
    }

    const targetMessage = messages.find((message) => message.id === messageId);
    if (!targetMessage || targetMessage.role !== 'assistant') {
      return;
    }

    setUpdatingFeedbackMessageId(messageId);
    try {
      const updatedMessage = await updateChippyMessageFeedback(activeThreadId, messageId, { feedbackComment });
      setMessages((prev) =>
        prev.map((message) =>
          message.id === messageId
            ? {
                ...message,
                feedback: updatedMessage.feedback ?? null,
              }
            : message
        )
      );
    } catch {
      // Do not interrupt chat flow if feedback persistence fails.
    } finally {
      setUpdatingFeedbackMessageId(null);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        minHeight: 'calc(100dvh - 70px)',
        display: 'flex',
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: '0.4rem', md: '1rem' },
        overflow: 'hidden',
        background: (theme) =>
          `radial-gradient(circle at 15% 20%, ${alpha(theme.palette.primaryLighter.main, 0.18)} 0%, ${alpha(
            theme.palette.primary.main,
            0.1
          )} 40%, ${theme.palette.whiteScaleLighter.main} 100%)`,
        position: 'relative',
        fontFamily: "'Space Grotesk', 'Avenir Next', 'Helvetica Neue', sans-serif",
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          pointerEvents: 'none',
          filter: 'blur(2px)',
          zIndex: 0,
          width: 260,
          height: 260,
          borderRadius: '42% 58% 65% 35%',
          background: (theme) =>
            `linear-gradient(145deg, ${theme.palette.tertiaryLight.main} 0%, ${theme.palette.tertiaryLighter.main} 100%)`,
          top: '18%',
          left: '-80px',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          pointerEvents: 'none',
          filter: 'blur(2px)',
          zIndex: 0,
          width: 280,
          height: 280,
          borderRadius: '54% 46% 34% 66%',
          background: (theme) =>
            `linear-gradient(145deg, ${theme.palette.primaryLight.main} 0%, ${theme.palette.whiteScaleLight.main} 100%)`,
          right: '-100px',
          bottom: '10%',
        }}
      />

      <Box
        sx={{
          width: 'min(1240px, 100%)',
          maxWidth: '1240px',
          mx: 'auto',
          height: { xs: 'calc(100dvh - 78px)', md: 'min(82dvh, 760px)' },
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 300px' },
          gap: { xs: '0.65rem', md: '0.85rem' },
          position: 'relative',
          zIndex: 1,
        }}
      >
        <ChippyChatWindow
          messages={messages}
          input={input}
          isTyping={isTyping}
          isLoadingMessages={isLoadingMessages}
          updatingFeedbackMessageId={updatingFeedbackMessageId}
          feedbackFormUrl={CHIPPY_FEEDBACK_FORM_URL}
          onInputChange={setInput}
          onSendMessage={sendMessage}
          onMessageFeedback={handleMessageFeedback}
          onMessageFeedbackComment={handleMessageFeedbackComment}
          onDownloadConversation={handleDownloadConversation}
        />

        <ChippyThreadsPanel
          threads={threads}
          activeThreadId={activeThreadId}
          isLoadingThreads={isLoadingThreads}
          onStartNewChat={handleStartNewChat}
          onSelectThread={(threadId) => {
            void loadThreadMessages(threadId);
          }}
        />
      </Box>
    </Box>
  );
};

export default ChippyPage;
